import React from 'react';
import { XIcon, CheckCircle2Icon, AlertTriangleIcon, ArrowRightIcon, FileTextIcon, CreditCardIcon, SparklesIcon } from 'lucide-react';
import { RecoSuggestion } from '../mocks/reconciliationData';
import { formatMoney } from '../../../shared/config/localeProfile';
interface ReconciliationDrawerProps {
  suggestion: RecoSuggestion | null;
  onClose: () => void;
  onAccept: (id: string) => void;
}
export function ReconciliationDrawer({
  suggestion,
  onClose,
  onAccept
}: ReconciliationDrawerProps) {
  if (!suggestion) return null;
  const totalPaymentGross = suggestion.payments.reduce((sum, p) => sum + p.grossAmount, 0);
  const totalPaymentFees = suggestion.payments.reduce((sum, p) => sum + p.feeAmount, 0);
  const totalPaymentNet = suggestion.payments.reduce((sum, p) => sum + p.netAmount, 0);
  const totalDocumentAmount = suggestion.documents.reduce((sum, d) => sum + d.amount, 0);
  return <>
      <div className="fixed inset-0 bg-gray-900/20 backdrop-blur-sm z-40" onClick={onClose} data-id="element-8944" />
      <div className="fixed inset-y-0 right-0 w-[500px] bg-white shadow-2xl z-50 flex flex-col border-l border-gray-200 transform transition-transform duration-300" data-id="element-8945">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gray-50" data-id="element-8946">
          <div data-id="element-8947">
            <h2 className="text-lg font-semibold text-gray-900" data-id="element-8948">
              Match Details
            </h2>
            <p className="text-sm text-gray-500 mt-0.5" data-id="element-8949">ID: {suggestion.id}</p>
          </div>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-200 transition-colors" data-id="element-8950">
            <XIcon size={20} data-id="element-8951" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-8" data-id="element-8952">
          {/* AI Rationale */}
          <div className="bg-blue-50 border border-blue-100 rounded-xl p-4" data-id="element-8953">
            <div className="flex items-start gap-3" data-id="element-8954">
              <SparklesIcon size={20} className="text-blue-600 mt-0.5" data-id="element-8955" />
              <div data-id="element-8956">
                <h3 className="text-sm font-semibold text-blue-900" data-id="element-8957">
                  AI Match Rationale
                </h3>
                <p className="text-sm text-blue-800 mt-1 leading-relaxed" data-id="element-8958">
                  {suggestion.rationale}
                </p>
                {suggestion.anomalyFlag && <div className="mt-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-amber-100 text-amber-800 text-xs font-medium" data-id="element-8959">
                    <AlertTriangleIcon size={14} data-id="element-8960" />
                    {suggestion.anomalyFlag}
                  </div>}
              </div>
            </div>
          </div>

          {/* Breakdown */}
          <div data-id="element-8961">
            <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-4" data-id="element-8962">
              Reconciliation Breakdown
            </h3>
            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden" data-id="element-8963">
              <div className="p-4 space-y-3" data-id="element-8964">
                <div className="flex justify-between text-sm" data-id="element-8965">
                  <span className="text-gray-600" data-id="element-8966">Gross Payment</span>
                  <span className="font-mono text-gray-900" data-id="element-8967">
                    {formatMoney(totalPaymentGross)}
                  </span>
                </div>
                <div className="flex justify-between text-sm" data-id="element-8968">
                  <span className="text-gray-600" data-id="element-8969">Fees / MDR</span>
                  <span className="font-mono text-red-600" data-id="element-8970">
                    -{formatMoney(totalPaymentFees)}
                  </span>
                </div>
                <div className="flex justify-between text-sm font-medium pt-3 border-t border-gray-100" data-id="element-8971">
                  <span className="text-gray-900" data-id="element-8972">Net Settlement</span>
                  <span className="font-mono text-gray-900" data-id="element-8973">
                    {formatMoney(totalPaymentNet)}
                  </span>
                </div>
              </div>
              <div className="bg-gray-50 p-4 border-t border-gray-200" data-id="element-8974">
                <div className="flex justify-between text-sm font-medium" data-id="element-8975">
                  <span className="text-gray-900" data-id="element-8976">Document Value</span>
                  <span className="font-mono text-gray-900" data-id="element-8977">
                    {formatMoney(totalDocumentAmount)}
                  </span>
                </div>
                <div className="flex justify-between text-sm mt-2" data-id="element-8978">
                  <span className="text-gray-500" data-id="element-8979">Variance (Adjusted)</span>
                  <span className="font-mono text-green-600" data-id="element-8980">
                    {formatMoney(totalPaymentGross - totalDocumentAmount)}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Linked Entities */}
          <div className="space-y-6" data-id="element-8981">
            {/* Payments */}
            <div data-id="element-8982">
              <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-3" data-id="element-8983">
                Payments ({suggestion.payments.length})
              </h3>
              <div className="space-y-2" data-id="element-8984">
                {suggestion.payments.map(p => <div key={p.id} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg bg-white" data-id="element-8985">
                    <div className="flex items-center gap-3" data-id="element-8986">
                      <div className="p-2 bg-gray-50 rounded-md" data-id="element-8987">
                        <CreditCardIcon size={16} className="text-gray-500" data-id="element-8988" />
                      </div>
                      <div data-id="element-8989">
                        <div className="text-sm font-medium text-gray-900" data-id="element-8990">
                          {p.reference}
                        </div>
                        <div className="text-xs text-gray-500" data-id="element-8991">
                          {p.method} • {new Date(p.date).toLocaleDateString()}
                        </div>
                      </div>
                    </div>
                    <div className="text-sm font-mono font-medium text-gray-900" data-id="element-8992">
                      {formatMoney(p.grossAmount)}
                    </div>
                  </div>)}
              </div>
            </div>

            <div className="flex justify-center" data-id="element-8993">
              <ArrowRightIcon size={20} className="text-gray-300 rotate-90" data-id="element-8994" />
            </div>

            {/* Documents */}
            <div data-id="element-8995">
              <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-3" data-id="element-8996">
                Documents ({suggestion.documents.length})
              </h3>
              <div className="space-y-2" data-id="element-8997">
                {suggestion.documents.map(d => <div key={d.id} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg bg-white" data-id="element-8998">
                    <div className="flex items-center gap-3" data-id="element-8999">
                      <div className="p-2 bg-gray-50 rounded-md" data-id="element-9000">
                        <FileTextIcon size={16} className="text-gray-500" data-id="element-9001" />
                      </div>
                      <div data-id="element-9002">
                        <div className="text-sm font-medium text-gray-900" data-id="element-9003">
                          {d.reference}
                        </div>
                        <div className="text-xs text-gray-500" data-id="element-9004">
                          {d.counterparty}
                        </div>
                      </div>
                    </div>
                    <div className="text-sm font-mono font-medium text-gray-900" data-id="element-9005">
                      {formatMoney(d.amount)}
                    </div>
                  </div>)}
              </div>
            </div>
          </div>

          {/* GL Coding */}
          <div data-id="element-9006">
            <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-3" data-id="element-9007">
              Bookkeeping
            </h3>
            <div className="bg-white border border-gray-200 rounded-xl p-4" data-id="element-9008">
              <label className="block text-xs font-medium text-gray-500 mb-1" data-id="element-9009">
                Suggested GL Category
              </label>
              <div className="text-sm font-medium text-gray-900" data-id="element-9010">
                {suggestion.glCategory || 'Uncategorized'}
              </div>
            </div>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="p-6 border-t border-gray-200 bg-gray-50 flex gap-3" data-id="element-9011">
          <button className="flex-1 px-4 py-2.5 bg-white border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors" data-id="element-9012">
            Adjust
          </button>
          <button className="flex-1 px-4 py-2.5 bg-white border border-red-200 text-red-600 text-sm font-medium rounded-lg hover:bg-red-50 transition-colors" data-id="element-9013">
            Reject
          </button>
          <button onClick={() => onAccept(suggestion.id)} className="flex-[2] flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors" data-id="element-9014">
            <CheckCircle2Icon size={18} data-id="element-9015" />
            Accept Match
          </button>
        </div>
      </div>
    </>;
}